import * as React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ChevronsUpDown, Check } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  getValidSubPurposes,
  getSubPurposeLabel,
  AI_MODEL_CATEGORIES,
  getCategoryLabel,
  type AiNodeType,
  type AiModelPurpose,
} from "@genieartinc/schemas";
import { cn } from "@/lib/utils";

interface ModelFamily {
  id: string;
  slug: string;
  name: string;
}

interface MetadataTabProps {
  modelTitle: string;
  modelDescription: string;
  selectedNodeType: string;
  selectedCategory: string;
  selectedModelFamilyId: string;
  selectedPurpose: string;
  selectedSubPurpose: string;
  enabled: boolean;
  featuredScore: number;
  modelFamilies: ModelFamily[];
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onNodeTypeChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onModelFamilyChange: (value: string) => void;
  onPurposeChange: (value: string) => void;
  onSubPurposeChange: (value: string) => void;
  onEnabledChange: (value: boolean) => void;
  onScoreChange: (value: number) => void;
}

export function MetadataTab({
  modelTitle,
  modelDescription,
  selectedNodeType,
  selectedCategory,
  selectedModelFamilyId,
  selectedPurpose,
  selectedSubPurpose,
  enabled,
  featuredScore,
  modelFamilies,
  onTitleChange,
  onDescriptionChange,
  onNodeTypeChange,
  onCategoryChange,
  onModelFamilyChange,
  onPurposeChange,
  onSubPurposeChange,
  onEnabledChange,
  onScoreChange,
}: MetadataTabProps) {
  const [categoryOpen, setCategoryOpen] = React.useState(false);
  const [modelFamilyOpen, setModelFamilyOpen] = React.useState(false);
  const [nodeTypeOpen, setNodeTypeOpen] = React.useState(false);
  const [purposeOpen, setPurposeOpen] = React.useState(false);
  const [subPurposeOpen, setSubPurposeOpen] = React.useState(false);

  const validSubPurposes = React.useMemo(() => {
    if (!selectedNodeType || !selectedPurpose || selectedPurpose === "none") return [];
    return getValidSubPurposes(selectedNodeType as AiNodeType, selectedPurpose as AiModelPurpose);
  }, [selectedNodeType, selectedPurpose]);

  React.useEffect(() => {
    if (selectedSubPurpose !== "none" && !validSubPurposes.includes(selectedSubPurpose)) {
      onSubPurposeChange("none");
    }
  }, [selectedPurpose, validSubPurposes, selectedSubPurpose, onSubPurposeChange]);

  return (
    <div className="space-y-4 mt-3">
      {/* Status & Score */}
      <div className="rounded-lg border p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-1.5">
            <Label htmlFor="enabled" className="text-xs font-medium">Status</Label>
            <div className="flex items-center gap-2">
              <Switch
                id="enabled"
                checked={enabled}
                onCheckedChange={onEnabledChange}
                className={enabled ? "data-[state=checked]:bg-green-600" : ""}
              />
              <span className="text-xs text-muted-foreground">
                {enabled ? "Model is enabled and available for use" : "Model is disabled"}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Enable or disable this model for node type selection
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="featuredScore" className="text-xs font-medium">
              Featured Score
            </Label>
            <Input
              id="featuredScore"
              type="number"
              min={0}
              max={100}
              value={featuredScore}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 0;
                onScoreChange(Math.min(100, Math.max(0, val)));
              }}
              className="h-8 text-xs"
            />
            <p className="text-[11px] text-muted-foreground">
              Priority ranking (0-100) for model ordering
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="modelTitle" className="text-xs">Title</Label>
        <Input
          id="modelTitle"
          value={modelTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Model title"
          className="h-8 text-xs"
        />
        <p className="text-[11px] text-muted-foreground">Display name for the model</p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="modelDescription" className="text-xs">Description</Label>
        <Input
          id="modelDescription"
          value={modelDescription}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder="Model description"
          className="h-8 text-xs"
        />
        <p className="text-[11px] text-muted-foreground">Brief description of what the model does</p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="modelFamily" className="text-xs">Model Family</Label>
        <Popover open={modelFamilyOpen} onOpenChange={setModelFamilyOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={modelFamilyOpen}
              className="w-full justify-between h-8 text-xs"
            >
              {selectedModelFamilyId === "none"
                ? "No family"
                : modelFamilies.find((f) => f.id === selectedModelFamilyId)?.name || "Select family"}
              <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[200px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search model family..." className="h-8 text-xs" />
              <CommandList>
                <CommandEmpty className="text-xs">No model family found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="none"
                    onSelect={() => {
                      onModelFamilyChange("none");
                      setModelFamilyOpen(false);
                    }}
                    className="text-xs"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-3 w-3",
                        selectedModelFamilyId === "none" ? "opacity-100" : "opacity-0"
                      )}
                    />
                    No family
                  </CommandItem>
                  {modelFamilies.map((family) => (
                    <CommandItem
                      key={family.id}
                      value={family.name}
                      onSelect={() => {
                        onModelFamilyChange(family.id);
                        setModelFamilyOpen(false);
                      }}
                      className="text-xs"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-3 w-3",
                          selectedModelFamilyId === family.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {family.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <p className="text-[11px] text-muted-foreground">AI model family classification (e.g., FLUX, SDXL, Stable Diffusion)</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="nodeType" className="text-xs">Node Type</Label>
          <Popover open={nodeTypeOpen} onOpenChange={setNodeTypeOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={nodeTypeOpen}
                className="w-full justify-between h-8 text-xs"
              >
                {selectedNodeType
                  ? selectedNodeType.charAt(0).toUpperCase() + selectedNodeType.slice(1)
                  : "Select node type"}
                <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[200px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search node type..." className="h-8 text-xs" />
                <CommandList>
                  <CommandEmpty className="text-xs">No node type found.</CommandEmpty>
                  <CommandGroup>
                    {["text-llm", "image", "video", "3d", "sound"].map((type) => (
                      <CommandItem
                        key={type}
                        value={type}
                        onSelect={() => {
                          onNodeTypeChange(type);
                          setNodeTypeOpen(false);
                        }}
                        className="text-xs"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-3 w-3",
                            selectedNodeType === type ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <p className="text-[11px] text-muted-foreground">Canvas node output type</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="category" className="text-xs">Category</Label>
          <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={categoryOpen}
                className="w-full justify-between h-8 text-xs"
              >
                {selectedCategory ? getCategoryLabel(selectedCategory) : "Select category"}
                <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[200px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search category..." className="h-8 text-xs" />
                <CommandList>
                  <CommandEmpty className="text-xs">No category found.</CommandEmpty>
                  <CommandGroup>
                    {AI_MODEL_CATEGORIES.map((category) => (
                      <CommandItem
                        key={category}
                        value={category}
                        onSelect={() => {
                          onCategoryChange(category);
                          setCategoryOpen(false);
                        }}
                        className="text-xs"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-3 w-3",
                            selectedCategory === category ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {getCategoryLabel(category)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <p className="text-[11px] text-muted-foreground">Model capability type (e.g., text-to-image)</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="purpose" className="text-xs">Purpose</Label>
          <Popover open={purposeOpen} onOpenChange={setPurposeOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={purposeOpen}
                className="w-full justify-between h-8 text-xs"
              >
                {selectedPurpose === "none"
                  ? "No purpose"
                  : selectedPurpose.charAt(0).toUpperCase() + selectedPurpose.slice(1)}
                <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[200px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search purpose..." className="h-8 text-xs" />
                <CommandList>
                  <CommandEmpty className="text-xs">No purpose found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="none"
                      onSelect={() => {
                        onPurposeChange("none");
                        setPurposeOpen(false);
                      }}
                      className="text-xs"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-3 w-3",
                          selectedPurpose === "none" ? "opacity-100" : "opacity-0"
                        )}
                      />
                      No purpose
                    </CommandItem>
                    {["create", "edit", "enhance", "tools"].map((purpose) => (
                      <CommandItem
                        key={purpose}
                        value={purpose}
                        onSelect={() => {
                          onPurposeChange(purpose);
                          setPurposeOpen(false);
                        }}
                        className="text-xs"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-3 w-3",
                            selectedPurpose === purpose ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {purpose.charAt(0).toUpperCase() + purpose.slice(1)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <p className="text-[11px] text-muted-foreground">Model&apos;s primary purpose (create/edit/enhance)</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="subPurpose" className="text-xs">Sub Purpose</Label>
          <Popover open={subPurposeOpen} onOpenChange={setSubPurposeOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={subPurposeOpen}
                className="w-full justify-between h-8 text-xs"
                disabled={!selectedPurpose || selectedPurpose === "none" || validSubPurposes.length === 0}
              >
                {selectedSubPurpose === "none"
                  ? "No sub-purpose"
                  : validSubPurposes.length === 0
                  ? "No sub-purposes available"
                  : getSubPurposeLabel(selectedSubPurpose)}
                <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[200px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search sub-purpose..." className="h-8 text-xs" />
                <CommandList>
                  <CommandEmpty className="text-xs">No sub-purpose found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="none"
                      onSelect={() => {
                        onSubPurposeChange("none");
                        setSubPurposeOpen(false);
                      }}
                      className="text-xs"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-3 w-3",
                          selectedSubPurpose === "none" ? "opacity-100" : "opacity-0"
                        )}
                      />
                      No sub-purpose
                    </CommandItem>
                    {validSubPurposes.map((sp) => (
                      <CommandItem
                        key={sp}
                        value={sp}
                        onSelect={() => {
                          onSubPurposeChange(sp);
                          setSubPurposeOpen(false);
                        }}
                        className="text-xs"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-3 w-3",
                            selectedSubPurpose === sp ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {getSubPurposeLabel(sp)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <p className="text-[11px] text-muted-foreground">Specific functionality within the purpose category</p>
        </div>
      </div>
    </div>
  );
}
